<template>
  <div
    class="balance panel bg-lighter-grey elevate text-center"
    :class="{ loading: !balance }"
  >
    <Notification
      v-if="error"
      icon-name="danger"
      :title="$gettext('Failed retrieving your balance')"
      :message="error"
    />

    <Loader v-else-if="!balance" />

    <template v-else>
      <p v-translate>
        Your Current Balance
      </p>
      <h2 class="h1 text-blue">
        {{ formatAmount(balance.totalBalance) }}
      </h2>

      <div class="bar-chart bg-light-purple">
        <div
          class="gauge"
          :style="`width: ${balance.availableBalance > 0 ? percentageAvailable : 0}%`"
        />
      </div>
      <div class="chart-legend row justify-between">
        <p class="available">
          <span class="dot bg-indigo" />
          <span v-translate>Available Balance:</span>
          <span>&nbsp;{{ formatAmount(balance.availableBalance) }}</span>
        </p>
        <p class="pending">
          <span class="dot bg-light-purple" />
          <span v-translate>Pending:</span>
          <span>&nbsp;{{ formatAmount(balance.frozenBalance) }}</span>
          <Popper>
            <Icon
              name="tooltip"
              fill="blue-alternate"
              class="tooltip-link"
            />
            <template #content>
              <Icon
                name="tooltip"
                fill="blue-alternate"
              />
              <p>
                {{ $gettext("You can withdraw these royalties once our licensing checks are complete. We'll let you know if there are any issues by email.") }}
                &nbsp;
                <a
                  v-translate
                  class="secondary"
                  href="https://dittom.us/pendingroyalties"
                  target="_blank"
                >
                  Learn more about pending royalties
                </a>.
              </p>
            </template>
          </Popper>
        </p>
      </div>

      <button
        v-if="isWithdrawalReady"
        v-translate
        @click="showPayoutDialog"
      >
        Withdraw
      </button>

      <PayoutRequestDialog
        v-if="isWithdrawalReady"
        ref="dialog"
        @payout-request-sent="onPayoutRequestSent"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import '@popperjs/core'
import Popper from 'vue3-popper'
import Icon from '@/components/CommonUI/Icon.vue'
import Loader from '@/components/CommonUI/Loader.vue'
import Notification from '@/components/CommonUI/Notification.vue'
import PayoutRequestDialog from '@/components/PayoutRequestDialog.vue'
import { formatAmount } from '@/helpers/formatAmount'
import { getErrorMessage } from '@/helpers/errorHandler'

const emit = defineEmits<{(e: 'payout-request-sent'): void}>()

const store = useStore()

const balance = ref<Balance | null>(null)
const error = ref<string | null>(null)
const dialog = ref<InstanceType<typeof PayoutRequestDialog> | null>(null)

const isWithdrawalReady = computed(() => store.getters.isWithdrawalReady)
const percentageAvailable = computed(() => (
  balance.value
    ? (100 * balance.value.availableBalance) / balance.value.totalBalance
    : 0
))

const getUserBalance = (): Promise<Balance> => store.dispatch('getUserBalance')
const fetchBalance = async (): Promise<void> => {
  balance.value = null
  error.value = null

  try {
    balance.value = await getUserBalance()
  } catch (err) {
    error.value = getErrorMessage(err)
  }
}

const showPayoutDialog = (): void => {
  if (!balance.value || !dialog.value) return
  dialog.value.show(balance.value.availableBalance)
}

const onPayoutRequestSent = (): void => {
  fetchBalance()
  emit('payout-request-sent')
}

onMounted(fetchBalance)
</script>

<style lang="scss">
// Popper Tooltip Plugin
:root {
  --popper-theme-background-color: #fff;
  --popper-theme-background-color-hover: #fff;
  --popper-theme-text-color: #000;
  --popper-theme-border-width: 0px;
  --popper-theme-border-style: solid;
  --popper-theme-border-radius: 2rem;
  --popper-theme-padding: 2rem;
  --popper-theme-box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.25);
}

// Component
.balance.panel {
  height: fit-content;
  color: var(--brand-body-light);

  .h1 {
    font-size: 4rem;

    @media (--max-small) {
      font-size: 3rem;
    }
  }

  > p,
  .chart-legend p {
    font-size: 1.25rem;
  }

  .tooltip-link {
    cursor: pointer;
    margin-left: 1rem;
    vertical-align: bottom;
    width: 1.25rem;
  }

  .popper {
    max-width: 260px;
    text-align: left;

    .icon {
      width: 1.6rem;
    }

    p {
      font-size: 0.75rem;
      line-height: 1.6;
      margin: 0;
    }
  }
}

.bar-chart,
.chart-legend {
  margin: 0 1rem;
}

.bar-chart {
  height: 2.25rem;
  border-radius: 1.125rem;
  overflow: hidden;

  .gauge {
    height: 100%;
    border-radius: 1.125rem 0 0 1.125rem;
    background-color: var(--light-indigo);
    background: linear-gradient(to right, var(--purple) 0%, var(--indigo) 100%)
  }
}

.chart-legend {
  .dot {
    display: inline-block;
    border-radius: 100%;
    width: 1.75rem;
    height: 1.75rem;
    margin-right: 20px;
    vertical-align: bottom;
  }
}
</style>
